// src/pages/Memorandums.tsx - COMPLETE FIXED VERSION WITH CLIENT-SIDE PDF DOWNLOAD
import { useState, useEffect } from 'react';
import { 
  Plus, Download, Eye, FileText, Clock, CheckCircle, User, RefreshCw, Zap,
  AlertCircle, Filter, Send, Calendar, MapPin, ExternalLink
} from 'lucide-react';
import { apiService, getAspirantId, API_BASE_URL } from '../services/api';
import type { Memorandum, MemorandumStatus } from '../types/memorandum';
import CreateMemorandumModal from '../components/CreateMemorandumModal';
import toast from 'react-hot-toast';

// ============ HELPER FUNCTIONS ============

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

// ============ PDF DOWNLOAD HELPERS ============

/**
 * Download memorandum as PDF (client-side generation - no backend needed)
 * Uses jsPDF like manifesto download
 */
const downloadMemorandumAsPDF = async (memorandum: any) => {
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(memorandum.subject, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Memo number and date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Memorandum Number: ${memorandum.number}`, margin, yPos);
    yPos += 5;
    doc.text(`Generated Date: ${new Date(memorandum.generatedAt).toLocaleDateString()}`, margin, yPos);
    yPos += 5;
    doc.text(`Status: ${memorandum.status.charAt(0).toUpperCase() + memorandum.status.slice(1)}`, margin, yPos);
    yPos += 10;

    // Recipient
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Recipient:', margin, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const recipientLines = doc.splitTextToSize(memorandum.recipient, pageWidth - margin * 2);
    doc.text(recipientLines, margin, yPos);
    yPos += recipientLines.length * 5 + 5;

    // Affected Wards
    if (memorandum.affectedWards && memorandum.affectedWards.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Affected Wards:', margin, yPos);
      yPos += 5;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const wardsText = memorandum.affectedWards.join(', ');
      const wardsLines = doc.splitTextToSize(wardsText, pageWidth - margin * 2);
      doc.text(wardsLines, margin, yPos);
      yPos += wardsLines.length * 5 + 5;
    }

    // Evidence Summary
    if (memorandum.evidence) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Evidence Summary:', margin, yPos);
      yPos += 5;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const evidenceLines = doc.splitTextToSize(memorandum.evidence, pageWidth - margin * 2);
      doc.text(evidenceLines, margin, yPos);
      yPos += evidenceLines.length * 5 + 5;
    }

    // Estimated Cost
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Estimated Cost:', margin, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(formatCurrency(memorandum.cost), margin, yPos);
    yPos += 10;

    // Content/Details
    if (memorandum.content) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Details:', margin, yPos);
      yPos += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const contentLines = doc.splitTextToSize(memorandum.content, pageWidth - margin * 2);
      for (let i = 0; i < contentLines.length; i++) {
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(contentLines[i], margin, yPos);
        yPos += 4;
      }
    }

    // Footer
    doc.setFontSize(8);
    doc.text(`Document ID: ${memorandum.id}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    doc.save(`${memorandum.number.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
    toast.success('Memorandum downloaded as PDF!');
    return true;
  } catch (pdfError) {
    console.warn('PDF generation failed, falling back to TXT:', pdfError);
    return downloadMemorandumAsTXT(memorandum);
  }
};

/**
 * Download memorandum as TXT (fallback)
 */
const downloadMemorandumAsTXT = (memorandum: any) => {
  try {
    let content = `========================================\n`;
    content += `${memorandum.subject.toUpperCase()}\n`;
    content += `========================================\n\n`;
    content += `MEMORANDUM NUMBER: ${memorandum.number}\n`;
    content += `GENERATED DATE: ${new Date(memorandum.generatedAt).toLocaleDateString()}\n`;
    content += `STATUS: ${memorandum.status.toUpperCase()}\n`;
    content += `\n`;

    content += `RECIPIENT:\n${memorandum.recipient}\n\n`;

    if (memorandum.affectedWards && memorandum.affectedWards.length > 0) {
      content += `AFFECTED WARDS:\n${memorandum.affectedWards.join(', ')}\n\n`;
    }

    if (memorandum.evidence) {
      content += `EVIDENCE SUMMARY:\n${memorandum.evidence}\n\n`;
    }

    content += `ESTIMATED COST:\n${formatCurrency(memorandum.cost)}\n\n`;

    if (memorandum.content) {
      content += `DETAILS:\n${memorandum.content}\n\n`;
    }

    content += `========================================\n`;
    content += `END OF MEMORANDUM\n`;
    content += `Document ID: ${memorandum.id}\n`;
    content += `Generated by PamojaConnect Platform\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${memorandum.number.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Memorandum downloaded as TXT file!');
    return true;
  } catch (error) {
    console.error('TXT download failed:', error);
    toast.error('Failed to download memorandum');
    return false;
  }
};

/**
 * Handle memorandum download (tries PDF first, falls back to TXT)
 */
const handleDownloadMemorandum = async (memorandum: any) => {
  toast.loading('Preparing download...');
  try {
    const success = await downloadMemorandumAsPDF(memorandum);
    if (success) {
      toast.dismiss();
      return;
    }
  } catch (error) {
    toast.dismiss();
    toast.loading('Falling back to text format...');
    try {
      const txtSuccess = downloadMemorandumAsTXT(memorandum);
      if (txtSuccess) {
        toast.dismiss();
        toast.success('Downloaded as text file');
      }
    } catch (txtError) {
      toast.dismiss();
      toast.error('Download failed. Please try again.');
    }
  }
};

// ============ MEMOCARD COMPONENT ============

// MemorandumCard Props interface
interface MemorandumCardProps {
  memorandum: {
    id: number;
    number: string;
    subject: string;
    recipient: string;
    cost: number;
    status: 'draft' | 'sent' | 'acknowledged' | 'completed';
    generatedAt: string;
    affectedWards: string[];
    evidence: string;
    downloadUrl?: string;
    type?: string;
    content?: string;
  };
  onDownload?: (id: number, number: string, memo: any) => void;
  onViewDetails?: (memorandum: any) => void;
  onSend?: (id: number) => void;
}

const MemorandumCard = ({ memorandum, onDownload, onViewDetails, onSend }: MemorandumCardProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-[#3c8d2e]/10 text-[#3c8d2e]';
      case 'sent': return 'bg-[#0b2045]/10 text-[#0b2045]';
      case 'acknowledged': return 'bg-[#fefce8] text-[#ca8a04]';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow hover:border-[#3c8d2e]/30">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="inline-block px-2 py-1 text-xs font-medium bg-[#f4f7f6] text-[#0b2045] rounded mb-2">
                  {memorandum.number}
                </span>
                <h3 className="text-lg font-semibold text-[#0b2045]">{memorandum.subject}</h3>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(memorandum.status)}`}>
                {memorandum.status.charAt(0).toUpperCase() + memorandum.status.slice(1)}
              </span>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-start">
                <FileText size={16} className="text-gray-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Recipient</p>
                  <p className="text-sm text-gray-600">{memorandum.recipient}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MapPin size={16} className="text-gray-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Affected Wards</p>
                  <p className="text-sm text-gray-600">
                    {memorandum.affectedWards.slice(0, 3).join(', ')}
                    {memorandum.affectedWards.length > 3 && ` +${memorandum.affectedWards.length - 3} more`}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Calendar size={16} className="text-gray-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Evidence</p>
                  <p className="text-sm text-gray-600">{memorandum.evidence}</p>
                </div>
              </div>

              <div className="flex items-start">
                <Clock size={16} className="text-gray-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Generated</p>
                  <p className="text-sm text-gray-600">{formatDate(memorandum.generatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between border-t pt-4">
          <div>
            <p className="text-2xl font-bold text-[#0b2045]">
              {formatCurrency(memorandum.cost)}
            </p>
            <p className="text-sm text-gray-600">Estimated Cost</p>
          </div>
          <div className="flex space-x-2">
            <button 
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-[#f4f7f6] transition-colors"
              onClick={() => onViewDetails && onViewDetails(memorandum)}
            >
              <Eye size={16} className="mr-1" />
              Details
            </button>
            <button 
              className="inline-flex items-center px-3 py-2 bg-[#0b2045] text-white rounded-lg text-sm font-medium hover:bg-[#152c53] transition-colors"
              onClick={() => onDownload && onDownload(memorandum.id, memorandum.number, memorandum)}
            >
              <Download size={16} className="mr-1" />
              Download
            </button>
            {memorandum.status === 'draft' && (
              <button 
                className="inline-flex items-center px-3 py-2 bg-[#3c8d2e] text-white rounded-lg text-sm font-medium hover:bg-[#2d7022] transition-colors"
                onClick={() => onSend && onSend(memorandum.id)}
              >
                <Send size={16} className="mr-1" />
                Send
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN MEMORANDUMS COMPONENT ============

const Memorandums = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [aspirantId, setAspirantId] = useState<number | null>(null);
  const [aspirantName, setAspirantName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [memorandums, setMemorandums] = useState<Memorandum[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    totalCost: 0,
    recentCount: 0
  });
  const [dataLoaded, setDataLoaded] = useState(false);

  // Initialize on mount - first get aspirant ID
  useEffect(() => {
    console.log('📋 Memorandum component mounted');
    loadAspirantData();
  }, []);

  // Fetch memorandums when aspirantId is available
  useEffect(() => {
    if (aspirantId) {
      fetchMemorandums();
    } else if (!dataLoaded) {
      // Only use mock data if we've tried and failed to get aspirantId
      console.log('⚠️ No aspirant ID available after load, using mock data');
      setMemorandums(getMockMemorandums());
      updateMockStats();
      setLoading(false);
      setDataLoaded(true);
    }
  }, [aspirantId]);

  const loadAspirantData = () => {
    try {
      const id = getAspirantId();
      console.log('🔍 Loaded aspirant ID:', id);
      
      if (id) {
        setAspirantId(id);
        
        // Get aspirant name
        const aspirantStr = localStorage.getItem('aspirant');
        const userStr = localStorage.getItem('user');
        
        if (aspirantStr) {
          try {
            const aspirantData = JSON.parse(aspirantStr);
            setAspirantName(aspirantData.name || aspirantData.fullName || 'Aspirant');
          } catch (e) {
            console.log('Error parsing aspirant data:', e);
          }
        } else if (userStr) {
          try {
            const userData = JSON.parse(userStr);
            setAspirantName(userData.fullName || userData.name || 'Aspirant');
          } catch (e) {
            console.log('Error parsing user data:', e);
          }
        }
      } else {
        console.log('❌ No aspirant ID found');
        setDataLoaded(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch aspirant data:', error);
      setDataLoaded(true);
      setLoading(false);
    }
  };

  const fetchMemorandums = async () => {
    if (!aspirantId) {
      console.log('❌ Cannot fetch memorandums: No aspirant ID');
      return;
    }

    setLoading(true);
    console.log(`🔄 Fetching memorandums for aspirant ID: ${aspirantId}`);
    
    try {
      let memorandumsData: Memorandum[] = [];
      let dataSource = '';

      // Try multiple endpoints in sequence
      
      // 1. Try /my-memorandums endpoint first
      try {
        console.log('📡 Trying endpoint: /my-memorandums');
        const response = await apiService.getMyMemorandums();
        console.log('📊 Response:', response);
        
        if (response.data?.success && Array.isArray(response.data.data)) {
          memorandumsData = response.data.data.map((memo: any) => ({
            id: memo.id,
            memorandumNumber: memo.memorandumNumber || `MEM-${memo.id}`,
            aspirantId: memo.aspirantId || aspirantId,
            aspirantName: memo.aspirantName || aspirantName,
            type: memo.type || 'COUNTY_GOVERNMENT',
            recipientDepartment: memo.recipientDepartment || '',
            subject: memo.subject || '',
            content: memo.content || '',
            evidenceSummary: memo.evidenceSummary || '',
            affectedWards: memo.affectedWards || [],
            estimatedCost: memo.estimatedCost || 0,
            costBreakdown: memo.costBreakdown || '',
            generatedAt: memo.generatedAt || new Date().toISOString(),
            downloadUrl: memo.downloadUrl || '',
            status: (memo.status || 'DRAFT').toUpperCase() as MemorandumStatus
          }));
          dataSource = '/my-memorandums';
          console.log(`✅ Loaded ${memorandumsData.length} memorandums from ${dataSource}`);
        }
      } catch (error) {
        console.log('⚠️ /my-memorandums failed:', error);
      }

      // 2. If first endpoint failed or returned no data, try /aspirant/{id}
      if (memorandumsData.length === 0) {
        try {
          console.log(`📡 Trying endpoint: /aspirant/${aspirantId}`);
          const response = await apiService.getAspirantMemorandums(aspirantId);
          console.log('📊 Response:', response);
          
          if (response.data?.success && Array.isArray(response.data.data)) {
            memorandumsData = response.data.data.map((memo: any) => ({
              id: memo.id,
              memorandumNumber: memo.memorandumNumber || `MEM-${memo.id}`,
              aspirantId: memo.aspirantId || aspirantId,
              aspirantName: memo.aspirantName || aspirantName,
              type: memo.type || 'COUNTY_GOVERNMENT',
              recipientDepartment: memo.recipientDepartment || '',
              subject: memo.subject || '',
              content: memo.content || '',
              evidenceSummary: memo.evidenceSummary || '',
              affectedWards: memo.affectedWards || [],
              estimatedCost: memo.estimatedCost || 0,
              costBreakdown: memo.costBreakdown || '',
              generatedAt: memo.generatedAt || new Date().toISOString(),
              downloadUrl: memo.downloadUrl || '',
              status: (memo.status || 'DRAFT').toUpperCase() as MemorandumStatus
            }));
            dataSource = `/aspirant/${aspirantId}`;
            console.log(`✅ Loaded ${memorandumsData.length} memorandums from ${dataSource}`);
          }
        } catch (error) {
          console.log(`⚠️ /aspirant/${aspirantId} failed:`, error);
        }
      }

      // 3. Try dashboard endpoint as last resort
      if (memorandumsData.length === 0) {
        try {
          console.log('📡 Trying dashboard endpoint for recent memorandums');
          const response = await apiService.getAspirantDashboard(aspirantId);
          console.log('📊 Dashboard response:', response);
          
          if (response.data?.success && response.data.data?.recentMemorandums) {
            memorandumsData = response.data.data.recentMemorandums.map((memo: any) => ({
              id: memo.id,
              memorandumNumber: memo.memorandumNumber || `MEM-${memo.id}`,
              aspirantId: memo.aspirantId || aspirantId,
              aspirantName: memo.aspirantName || aspirantName,
              type: memo.type || 'COUNTY_GOVERNMENT',
              recipientDepartment: memo.recipientDepartment || '',
              subject: memo.subject || '',
              content: memo.content || '',
              evidenceSummary: memo.evidenceSummary || '',
              affectedWards: memo.affectedWards || [],
              estimatedCost: memo.estimatedCost || 0,
              costBreakdown: memo.costBreakdown || '',
              generatedAt: memo.generatedAt || new Date().toISOString(),
              downloadUrl: memo.downloadUrl || '',
              status: (memo.status || 'DRAFT').toUpperCase() as MemorandumStatus
            }));
            dataSource = 'dashboard';
            console.log(`✅ Loaded ${memorandumsData.length} memorandums from ${dataSource}`);
          }
        } catch (error) {
          console.log('⚠️ Dashboard endpoint failed:', error);
        }
      }

      if (memorandumsData.length > 0) {
        setMemorandums(memorandumsData);
        
        // Calculate stats
        const totalCost = memorandumsData.reduce((sum, memo) => sum + (memo.estimatedCost || 0), 0);
        setStats({
          total: memorandumsData.length,
          totalCost,
          recentCount: memorandumsData.filter(m => {
            const date = new Date(m.generatedAt);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return date > thirtyDaysAgo;
          }).length
        });
        
        toast.success(`Loaded ${memorandumsData.length} memorandums`);
      } else {
        console.log('⚠️ No memorandums found from any endpoint');
        setMemorandums([]);
        setStats({
          total: 0,
          totalCost: 0,
          recentCount: 0
        });
        
        // Show info toast
        toast.success('No memorandums found. Create your first one!');
      }
    } catch (error: any) {
      console.error('❌ Failed to fetch memorandums:', error);
      
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      
      // Show empty state
      setMemorandums([]);
      setStats({
        total: 0,
        totalCost: 0,
        recentCount: 0
      });
      
      toast.error('Failed to load memorandums from server');
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  };

  const getMockMemorandums = (): Memorandum[] => {
    return [
      {
        id: 1,
        memorandumNumber: 'MEM-CTG-0001-12345',
        subject: 'Acute Water Shortage in London, Kapkures, and Biashara Wards',
        recipientDepartment: 'County Executive Committee Member - Water, Environment & Natural Resources',
        estimatedCost: 4500000,
        status: 'DRAFT',
        generatedAt: new Date().toISOString(),
        affectedWards: ['London Ward', 'Kapkures Ward', 'Biashara Ward'],
        evidenceSummary: '4,312 citizen reports received via platform showing severe water scarcity affecting over 10,000 households',
        type: 'COUNTY_GOVERNMENT',
        downloadUrl: '#',
        aspirantId: aspirantId || 1,
        aspirantName: aspirantName || 'Aspirant User',
        content: 'Full memorandum content detailing the water crisis and proposed solutions',
        costBreakdown: 'Water bowsers: KES 1.2M, Pipeline repairs: KES 2.1M, Emergency tanks: KES 1.2M'
      },
      {
        id: 2,
        memorandumNumber: 'MEM-CAS-0002-12346',
        subject: 'Road Repair and Maintenance in Gilgil Constituency',
        recipientDepartment: 'County Executive Committee Member - Transport & Infrastructure',
        estimatedCost: 2800000,
        status: 'SENT',
        generatedAt: new Date(Date.now() - 86400000).toISOString(),
        affectedWards: ['Gilgil Ward', 'Elementaita Ward'],
        evidenceSummary: '2,189 reports on poor road conditions affecting transportation and business activities',
        type: 'COUNTY_ASSEMBLY',
        downloadUrl: '#',
        aspirantId: aspirantId || 1,
        aspirantName: aspirantName || 'Aspirant User',
        content: 'Request for urgent road repairs and maintenance program',
        costBreakdown: 'Grading equipment: KES 0.8M, Materials: KES 1.2M, Labor: KES 0.8M'
      }
    ];
  };

  const updateMockStats = () => {
    const mockData = getMockMemorandums();
    const totalCost = mockData.reduce((sum: number, memo: Memorandum) => sum + (memo.estimatedCost || 0), 0);
    setStats({
      total: mockData.length,
      totalCost,
      recentCount: mockData.length
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    
    // Reload aspirant data first
    const id = getAspirantId();
    if (id) {
      setAspirantId(id);
      await fetchMemorandums();
    } else {
      toast.error('No aspirant ID found. Please login again.');
    }
    
    setRefreshing(false);
    toast.success('Memorandum list refreshed!');
  };

  // Quick memorandum functions
  const createQuickWaterMemorandum = async () => {
    if (!aspirantId) {
      toast.error('Please login as an aspirant first');
      return;
    }
    
    setRefreshing(true);
    
    const requestData = {
      aspirantId: aspirantId,
      type: 'COUNTY_GOVERNMENT' as const,
      recipientDepartment: 'County Executive Committee Member - Water, Environment & Natural Resources',
      subject: 'Emergency Water Shortage Response',
      content: 'EVIDENCE SUMMARY:\nBased on citizen reports via platform. Acute water shortage affecting multiple wards.\n\nPROPOSED ACTIONS:\n1. Immediate deployment of water bowsers\n2. Emergency pipeline repairs\n3. Coordination with water service providers',
      evidenceSummary: 'Based on citizen reports via platform. Acute water shortage affecting multiple wards.',
      affectedWards: ['London Ward', 'Biashara Ward'],
      estimatedCost: 2800000,
      costBreakdown: 'To be detailed upon further assessment',
      useTemplate: false
    };
    
    try {
      await apiService.createMemorandum(requestData);
      toast.success('Water memorandum created successfully!');
      fetchMemorandums();
    } catch (error) {
      toast.error('Failed to create memorandum. Please try the full form.');
    } finally {
      setRefreshing(false);
    }
  };

  const createQuickRoadsMemorandum = async () => {
    if (!aspirantId) {
      toast.error('Please login as an aspirant first');
      return;
    }
    
    setRefreshing(true);
    
    const requestData = {
      aspirantId: aspirantId,
      type: 'COUNTY_GOVERNMENT' as const,
      recipientDepartment: 'County Executive Committee Member - Transport & Infrastructure',
      subject: 'Road Infrastructure Improvement',
      content: 'EVIDENCE SUMMARY:\nMultiple reports of deteriorating road conditions and poor drainage.\n\nPROPOSED ACTIONS:\n1. Grading of main access roads\n2. Drainage clearance and repair\n3. Pothole filling program',
      evidenceSummary: 'Multiple reports of deteriorating road conditions and poor drainage.',
      affectedWards: ['Gilgil Ward', 'Naivasha East'],
      estimatedCost: 5000000,
      costBreakdown: 'To be detailed upon further assessment',
      useTemplate: false
    };
    
    try {
      await apiService.createMemorandum(requestData);
      toast.success('Roads memorandum created successfully!');
      fetchMemorandums();
    } catch (error) {
      toast.error('Failed to create memorandum. Please try the full form.');
    } finally {
      setRefreshing(false);
    }
  };

  // SIMPLIFIED: Download function - just calls handleDownloadMemorandum
  const handleDownload = async (memorandumId: number, memorandumNumber: string, memorandum: any) => {
    handleDownloadMemorandum(memorandum);
  };

  const handleViewDetails = (memorandum: any) => {
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex flex-col`}>
        <div className="p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <FileText className="h-6 w-6 text-[#0b2045]" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-[#0b2045]">{memorandum.subject}</p>
              <div className="mt-1">
                <p className="text-sm text-gray-600"><strong>To:</strong> {memorandum.recipient}</p>
                <p className="text-sm text-gray-600"><strong>Memo #:</strong> {memorandum.number}</p>
                <p className="text-sm text-gray-600"><strong>Status:</strong> 
                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                    memorandum.status === 'completed' ? 'bg-[#3c8d2e]/10 text-[#3c8d2e]' :
                    memorandum.status === 'sent' ? 'bg-[#0b2045]/10 text-[#0b2045]' :
                    memorandum.status === 'acknowledged' ? 'bg-[#fefce8] text-[#ca8a04]' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {memorandum.status.charAt(0).toUpperCase() + memorandum.status.slice(1)}
                  </span>
                </p>
                <p className="text-sm text-gray-600"><strong>Cost:</strong> {formatCurrency(memorandum.cost)}</p>
                {memorandum.affectedWards && memorandum.affectedWards.length > 0 && (
                  <p className="text-sm text-gray-600"><strong>Affected Wards:</strong> {memorandum.affectedWards.join(', ')}</p>
                )}
              </div>
              <div className="mt-2 text-sm text-gray-700">
                <p className="font-medium">Evidence Summary:</p>
                <p className="mt-1">{memorandum.evidence}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200">
          <div className="flex justify-between p-3">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                handleDownloadMemorandum(memorandum);
              }}
              className="px-3 py-1.5 text-sm font-medium text-[#0b2045] hover:text-[#152c53]"
            >
              Download
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    ), {
      duration: 10000,
    });
  };

  const handleSendMemorandum = async (memorandumId: number) => {
    try {
      // Update memorandum status to 'SENT'
      await apiService.updateMemorandumStatus(memorandumId.toString(), 'SENT');
      toast.success('Memorandum sent successfully!');
      fetchMemorandums();
    } catch (error) {
      console.error('Failed to send memorandum:', error);
      toast.error('Failed to send memorandum. Please try again.');
    }
  };

  // Convert Memorandum to MemorandumCard format
  const convertToCardFormat = (memorandum: Memorandum): any => {
    const statusMap: Record<string, 'draft' | 'sent' | 'acknowledged' | 'completed'> = {
      'DRAFT': 'draft',
      'SENT': 'sent',
      'ACKNOWLEDGED': 'acknowledged',
      'COMPLETED': 'completed'
    };

    return {
      id: memorandum.id,
      number: memorandum.memorandumNumber,
      subject: memorandum.subject,
      recipient: memorandum.recipientDepartment,
      cost: memorandum.estimatedCost || 0,
      status: statusMap[memorandum.status] || 'draft',
      generatedAt: memorandum.generatedAt,
      affectedWards: memorandum.affectedWards || [],
      evidence: memorandum.evidenceSummary || 'No evidence summary',
      downloadUrl: memorandum.downloadUrl,
      type: memorandum.type,
      content: memorandum.content
    };
  };

  const filteredMemorandums = filter === 'all' 
    ? memorandums 
    : memorandums.filter(memo => {
        const statusMap: Record<string, string> = {
          'DRAFT': 'draft',
          'SENT': 'sent',
          'ACKNOWLEDGED': 'acknowledged',
          'COMPLETED': 'completed'
        };
        const lowerStatus = statusMap[memo.status] || memo.status.toLowerCase();
        return lowerStatus === filter.toLowerCase();
      });

  const statusCounts = {
    all: memorandums.length,
    draft: memorandums.filter(m => m.status === 'DRAFT').length,
    sent: memorandums.filter(m => m.status === 'SENT').length,
    acknowledged: memorandums.filter(m => m.status === 'ACKNOWLEDGED').length,
    completed: memorandums.filter(m => m.status === 'COMPLETED').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0b2045] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading memorandums...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0b2045]">My Memorandums</h1>
          <p className="text-gray-600">Generate evidence-based policy memorandums</p>
          {aspirantName && (
            <p className="text-sm text-gray-500 mt-1">
              Created by: <span className="font-medium">{aspirantName}</span> (ID: {aspirantId})
            </p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {aspirantName && (
            <div className="hidden sm:flex items-center px-3 py-2 bg-[#0b2045]/10 text-[#0b2045] rounded-lg text-sm">
              <User size={16} className="mr-2" />
              {aspirantName}
            </div>
          )}
          <div className="flex space-x-2">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-3 py-2 bg-[#3c8d2e] text-white rounded-lg text-sm font-medium hover:bg-[#2d7022] transition-colors disabled:opacity-50"
              disabled={refreshing}
            >
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-[#0b2045] text-white rounded-lg text-sm font-medium hover:bg-[#152c53] transition-colors"
            >
              <Plus size={16} className="mr-2" />
              Create Memorandum
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-[#0b2045]/5 to-[#0b2045]/10 border border-[#0b2045]/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Memorandums</p>
              <p className="text-2xl font-bold text-[#0b2045]">{stats.total}</p>
            </div>
            <FileText className="h-8 w-8 text-[#0b2045]" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Generated from citizen reports</p>
        </div>
        
        <div className="bg-gradient-to-r from-[#3c8d2e]/5 to-[#3c8d2e]/10 border border-[#3c8d2e]/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Estimated Cost</p>
              <p className="text-2xl font-bold text-[#0b2045]">{formatCurrency(stats.totalCost)}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-[#3c8d2e]" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Combined value of all memorandums</p>
        </div>
        
        <div className="bg-gradient-to-r from-[#0b2045]/5 to-[#0b2045]/10 border border-[#0b2045]/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Recent Activity</p>
              <p className="text-2xl font-bold text-[#0b2045]">{stats.recentCount}</p>
            </div>
            <Clock className="h-8 w-8 text-[#0b2045]" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Created in last 30 days</p>
        </div>
      </div>

      {/* Quick Create Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div 
          className="bg-gradient-to-r from-[#0b2045]/5 to-[#0b2045]/10 border border-[#0b2045]/20 rounded-xl p-5 hover:shadow-md transition-all cursor-pointer"
          onClick={() => setShowCreateModal(true)}
        >
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-[#0b2045] rounded-lg flex items-center justify-center mr-3">
              <Zap size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#0b2045]">Full Memorandum</h3>
              <p className="text-sm text-gray-600">Step-by-step form</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            Use the detailed form to create a customized memorandum with all required information
          </p>
          <div className="text-xs text-gray-500">
            Click to open create form
          </div>
        </div>
        
        <div 
          className="bg-gradient-to-r from-[#3c8d2e]/5 to-[#3c8d2e]/10 border border-[#3c8d2e]/20 rounded-xl p-5 hover:shadow-md transition-all cursor-pointer"
          onClick={createQuickWaterMemorandum}
          style={{ opacity: refreshing ? 0.7 : 1, pointerEvents: refreshing ? 'none' : 'auto' }}
        >
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-[#3c8d2e] rounded-lg flex items-center justify-center mr-3">
              <FileText size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#0b2045]">Quick Water Memo</h3>
              <p className="text-sm text-gray-600">One-click creation</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            Create a water shortage memorandum instantly with pre-filled data
          </p>
          <div className="text-xs text-gray-500">
            {refreshing ? 'Creating...' : 'Click to create immediately'}
          </div>
        </div>
        
        <div 
          className="bg-gradient-to-r from-[#ca8a04]/5 to-[#ca8a04]/10 border border-[#ca8a04]/20 rounded-xl p-5 hover:shadow-md transition-all cursor-pointer"
          onClick={createQuickRoadsMemorandum}
          style={{ opacity: refreshing ? 0.7 : 1, pointerEvents: refreshing ? 'none' : 'auto' }}
        >
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-[#ca8a04] rounded-lg flex items-center justify-center mr-3">
              <FileText size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#0b2045]">Quick Roads Memo</h3>
              <p className="text-sm text-gray-600">One-click creation</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            Create a roads and drainage memorandum with one click
          </p>
          <div className="text-xs text-gray-500">
            {refreshing ? 'Creating...' : 'Click to create immediately'}
          </div>
        </div>
      </div>

      {/* Status Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {['draft', 'sent', 'acknowledged', 'completed', 'all'].map((status) => (
          <div 
            key={status} 
            className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${
              filter === status ? 'ring-2 ring-[#0b2045]' : ''
            }`}
            onClick={() => setFilter(status)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 capitalize">{status}</p>
                <p className="text-xl font-bold text-[#0b2045]">
                  {statusCounts[status as keyof typeof statusCounts]}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${
                status === 'draft' ? 'bg-gray-100' :
                status === 'sent' ? 'bg-[#0b2045]/10' :
                status === 'acknowledged' ? 'bg-[#fefce8]' :
                status === 'completed' ? 'bg-[#3c8d2e]/10' :
                'bg-[#f4f7f6]'
              }`}>
                {status === 'draft' && <FileText size={16} className="text-gray-600" />}
                {status === 'sent' && <Send size={16} className="text-[#0b2045]" />}
                {status === 'acknowledged' && <Clock size={16} className="text-[#ca8a04]" />}
                {status === 'completed' && <CheckCircle size={16} className="text-[#3c8d2e]" />}
                {status === 'all' && <FileText size={16} className="text-gray-600" />}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <div className="flex flex-wrap items-center gap-2">
          <Filter size={16} className="text-gray-500" />
          <span className="text-sm text-gray-600 mr-2">Filter by status:</span>
          {['all', 'draft', 'sent', 'acknowledged', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                filter === status
                  ? 'bg-[#0b2045] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-[#f4f7f6]'
              }`}
            >
              {status} ({statusCounts[status as keyof typeof statusCounts]})
            </button>
          ))}
        </div>
      </div>

      {/* Memorandums List with Cards */}
      {filteredMemorandums.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredMemorandums.map((memorandum) => (
            <MemorandumCard
              key={memorandum.id}
              memorandum={convertToCardFormat(memorandum)}
              onDownload={handleDownload}
              onViewDetails={handleViewDetails}
              onSend={handleSendMemorandum}
            />
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
          <div className="w-16 h-16 bg-[#f4f7f6] rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="text-[#0b2045]" size={24} />
          </div>
          <h3 className="text-lg font-semibold text-[#0b2045] mb-2">No memorandums found</h3>
          <p className="text-gray-600 mb-6">
            {filter === 'all' 
              ? 'You haven\'t generated any memorandums yet.' 
              : `No ${filter} memorandums found.`}
          </p>
          {aspirantId ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-[#0b2045] text-white rounded-lg text-sm font-medium hover:bg-[#152c53] transition-colors"
            >
              <Plus size={16} className="mr-2" />
              Generate Your First Memorandum
            </button>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-4">Log in as an aspirant to create memorandums</p>
              <a
                href="/login"
                className="inline-flex items-center px-4 py-2 bg-[#0b2045] text-white rounded-lg text-sm font-medium hover:bg-[#152c53] transition-colors"
              >
                Go to Login
              </a>
            </div>
          )}
        </div>
      )}

      {/* Create Memorandum Modal */}
      {showCreateModal && (
        <CreateMemorandumModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            fetchMemorandums();
            toast.success('Memorandum created successfully!');
          }}
        />
      )}
    </div>
  );
};

export default Memorandums;